import { graphql, useStaticQuery } from 'gatsby';
import pt from 'prop-types';
import React, { useEffect, useState } from 'react';
import Hero from '../../components/cms-modules/hero';
import Icon from '../../components/ui/icon';
import { HeadlineProvider } from '../../context/HeadlineProvider/HeadlineProvider';
import { useLocale } from '../../context/locale.context';
import { MicrocopyContext } from '../../context/microcopy.context';
import { PreviewContext } from '../../context/preview.context';
import { getCopy } from '../../helpers/copy';
import { getAPIRoot, getDebugInfo, refreshData } from '../../helpers/fetch';
import { combine } from '../../helpers/styles';
import MaintenancePage from '../../pages/maintenance';
import CookieBanner from '../cookieBanner';
import Footer from '../footer';
import Header from '../header';
import SEO from '../seo';
import * as styles from './layout.module.scss';
import AnchorLinkNavigation from '../anchorLinkNavigation';
import ScrollToTopButton from '../scrollToTop';
import { proptypes as heroTeaserProptypes } from '../../components/cms-modules/hero/components/heroTeaser';

const Layout = ({
  children,
  title,
  description,
  heroTeaser,
  heroCarouselItems,
  heroCarouselTeaser,
  hasWhiteNavigation,
  metaNaviWhite,
  preview,
  isFooterBeige,
  previewImage,
  dynamicPagePreview,
  anchorLinks,
}) => {
  const [apiRoot, setApiRoot] = useState('');
  const [debugInfo, setDebugInfo] = useState({});
  const [hintMinimized, setHintMinimized] = useState(false);
  const [dataUpdating, setDataUpdating] = useState(false);
  const { locale } = useLocale();
  const {
    allContentfulSiteSettings: settingsRaw,
    allContentfulMicrocopy,
    currentBuildDate,
  } = useStaticQuery(
    graphql`
      fragment link on ContentfulLink {
        externalLink
        text: label
        linkToPage {
          ... on ContentfulPressRelease {
            slug
            internal {
              type
            }
          }
          ... on ContentfulArticle {
            slug
            internal {
              type
            }
          }
          ... on ContentfulPage {
            slug
            internal {
              type
            }
          }
        }
        linkToDynamicPage
        dynamicDetailPageId
        dynamicDetailPageName
        queryParameter
        downloadDocument {
          ...document
        }
        icon
        type
        transparent: isTransparent
      }

      fragment form on ContentfulContentForm {
        inputLabel: label
        inputPlaceholder
        btnLabel
        type
        action
      }

      query {
        currentBuildDate {
          currentDate
        }
        allContentfulSiteSettings {
          nodes {
            maintenanceMode
            node_locale
            siteName
            siteAuthor
            copyrightText
            firstColumnHeadline
            firstColumnLinks {
              ...link
            }
            metaLinksWhite
            mainNavigation {
              link {
                ...link
              }
              childItems {
                link {
                  ...link
                }
              }
            }
            metaNavigation {
              ...link
            }
            secondColumnHeadline
            secondColumnLinks {
              ...link
            }
            socialLinks {
              ...link
            }
            newsletter {
              ...form
            }
            footerIsBeige
            previewImage {
              title
              description
              file {
                url
                contentType
              }
              gatsbyImageData(
                layout: CONSTRAINED
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
        }

        allContentfulMicrocopy {
          nodes {
            node_locale
            key
            value
          }
        }
      }
    `
  );

  const updateDataAndRefresh = () => {
    setDataUpdating(true);
    refreshData();
    setTimeout(() => {
      window.location.reload();
    }, 8000);
  };

  const [microcopy, setMicrocopy] = useState(() =>
    allContentfulMicrocopy.nodes
      .filter((node) => node.node_locale === locale)
      .reduce((acc, curr) => {
        return {
          ...acc,
          [curr.key]: curr.value,
        };
      }, {})
  );

  const siteSettings = settingsRaw.nodes.filter(
    (node) => node.node_locale === locale
  )[0];

  const getApiRootForState = async () => {
    const url = await getAPIRoot();
    const info = await getDebugInfo();

    setApiRoot(url);
    setDebugInfo(info);
  };

  useEffect(() => {
    const isIphone = /(iPhone)/g.test(navigator?.userAgent);
    if (isIphone) {
      // Needed to get the correct height on iOS
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    getApiRootForState();
  }, []);

  if (
    siteSettings?.maintenanceMode &&
    process.env.GATSBY_MAINTENANCE_CAPABLE === 'true'
  ) {
    return <MaintenancePage />;
  }

  return (
    <MicrocopyContext.Provider value={microcopy}>
      <PreviewContext.Provider value={preview}>
        <HeadlineProvider>
          {!dynamicPagePreview && (
            <>
              {(process.env.NODE_ENV === 'development' ||
                process.env.GATSBY_CONTENTFUL_PREVIEW) && (
                <>
                  <button
                    style={{ position: 'fixed', bottom: '5px', right: '5px' }}
                    onClick={updateDataAndRefresh}
                  >
                    {dataUpdating
                      ? 'One moment, please...'
                      : `Updated at ${currentBuildDate.currentDate}. Update`}
                  </button>
                </>
              )}
              <CookieBanner />
              <SEO
                title={`${siteSettings.siteName} - ${getCopy(
                  title,
                  microcopy
                )}`}
                lang={locale}
                description={getCopy(description, microcopy)}
                author={siteSettings.siteAuthor}
                imageUrl={previewImage || siteSettings.previewImage?.file?.url}
              />
              <Header
                siteSettings={siteSettings}
                hasHero={heroTeaser !== null || heroCarouselItems !== null}
                metaNaviWhite={metaNaviWhite}
                hasWhiteNavigation={hasWhiteNavigation}
              />
              {heroTeaser && (
                <Hero
                  backgroundImageLink={heroTeaser.backgroundImageLink}
                  background={heroTeaser.background}
                  heroTeaser={heroTeaser.heroTeaser}
                  disableGatsbyImage={preview}
                />
              )}
              {heroCarouselItems?.length > 0 && (
                <Hero
                  items={heroCarouselItems}
                  carouselTeaser={heroCarouselTeaser}
                  disableGatsbyImage={preview}
                />
              )}
              {anchorLinks.length > 0 && (
                <AnchorLinkNavigation items={anchorLinks} />
              )}
              <ScrollToTopButton className={styles.scrollToTopButton} />
              {apiRoot.includes('search-') && debugInfo && (
                <div
                  style={{ '--url': apiRoot }}
                  className={combine(
                    styles.hint,
                    hintMinimized && styles.minimized
                  )}
                >
                  <div className={styles.header}>
                    <h3>
                      {apiRoot
                        .match(/search-\w+?(?=.azurewebsites)/)?.[0]
                        .split('-')
                        .pop()
                        .toUpperCase()}
                    </h3>
                    <div className={styles.actions}>
                      <Icon
                        onClick={() => setHintMinimized(!hintMinimized)}
                        name={
                          hintMinimized ? 'window-maximize' : 'window-minimize'
                        }
                      />
                      <Icon onClick={() => setDebugInfo(null)} name="times" />
                    </div>
                  </div>
                  <ul className={styles.debugInfo}>
                    <li>backendUrl: {apiRoot}</li>
                    {Object.entries(debugInfo).map(([key, val]) => (
                      <li>
                        {key}: {val.toString()}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          <main
            className={combine(
              !heroTeaser && !heroCarouselItems && styles.withoutHero,
              dynamicPagePreview && styles.preview
            )}
          >
            {children}
          </main>
          {!dynamicPagePreview && (
            <Footer siteSettings={siteSettings} isFooterBeige={isFooterBeige} />
          )}
        </HeadlineProvider>
      </PreviewContext.Provider>
    </MicrocopyContext.Provider>
  );
};

Layout.propTypes = {
  heroTeaser: pt.shape(heroTeaserProptypes),
  heroCarouselTeaser: pt.shape(heroTeaserProptypes),
  hasWhiteNavigation: pt.bool,
  title: pt.string.isRequired,
  description: pt.string.isRequired,
  preview: pt.bool,
  isFooterBeige: pt.bool,
  metaNaviWhite: pt.bool,
  heroCarouselItems: pt.array,
  anchorLinks: pt.arrayOf(
    pt.shape({
      label: pt.string,
      anchorId: pt.string,
    })
  ),
};

Layout.defaultProps = {
  heroTeaser: null,
  hasWhiteNavigation: false,
  preview: false,
  isFooterBeige: false,
  metaNaviWhite: false,
  heroCarouselItems: [],
  anchorLinks: [],
};

export default Layout;
